/**
 * Module dependencies
 */

import _ from 'lodash';
import { format, zonedTimeToUtc } from 'date-fns-tz';

/**
 * Basic type definitions.
 *
 * Roughly based on https://github.com/bishopZ/Typecast.js
 * ________________________________________________________________________________
 *
 * @type {object}
 */

const TypeCoerce = {

  //          888            d8b
  //          888            Y8P
  //          888
  // .d8888b  888888 888d888 888 88888b.   .d88b.
  // 88K      888    888P"   888 888 "88b d88P"88b
  // "Y8888b. 888    888     888 888  888 888  888
  //      X88 Y88b.  888     888 888  888 Y88b 888
  //  88888P'  "Y888 888     888 888  888  "Y88888
  //                                           888
  //                                      Y8b d88P
  //                                       "Y88P"
  string: {
    id: 'string',
    description: 'a string',
    is: _.isString,
    to(value) {

      if (_.isString(value)) {
        return value;
      }

      // Convert date into zone-independent (UTC) ISO-8601 timestamp
      if (_.isDate(value)) {
        return value.toJSON();
      }

      if (_.isUndefined(value)) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }

      if (value instanceof Function) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }

      if (value instanceof Object) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }

      if (value instanceof Array) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }

      // Reject RttcRefPlaceholders
      // (this is a special case so there is a placeholder value that ONLY validates against the "ref" type)
      if (_.isObject(value) && value.constructor.name === 'RttcRefPlaceholder') {
        throw new Error('E_runtimeInputTypeCoercionError');
      }

      if (value === Infinity) {
        throw new Error('E_runtimeInputTypeCoercionError');
      }

      if (value === -Infinity) {
        throw new Error('E_runtimeInputTypeCoercionError');
      }

      if (_.isNaN(value)) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }

      if (_.isNull(value)) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }

      return String(value);
    },
    format(value, pattern) {
      let i = 0;
      const valueString = this.to(value);
      return pattern.replaceAll('#', () => valueString[i++]);
    },
    getBase() {
      return '';
    }
  },

  //      888          888
  //      888          888
  //      888          888
  //  .d88888  8888b.  888888 .d88b.
  // d88" 888     "88b 888   d8P  Y8b
  // 888  888 .d888888 888   88888888
  // Y88b 888 888  888 Y88b. Y8b.
  //  "Y88888 "Y888888  "Y888 "Y8888
  date: {
    id: 'date',
    description: 'a date or date/time',
    is(value) {
      return _.isDate(value);
    },
    to(value, tz) {
      if (_.isString(value) || _.isNumber(value)) {
        try {
          value = new Date(value);
        } catch (err) {
          console.warn(`Cannot parse as date:${value} - ${err.message}`);
          throw new TypeError('E_runtimeInputTypeCoercionError');
        }
      }
      if (_.isDate(value)) {
        return tz ?
          zonedTimeToUtc(value, tz) :
          value;
      }
      if (_.isObject(value)) {
        console.warn(`Trying to parse Date on an object: ${value}`);
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }
      return this.getBase();
    },
    format(value, dateFormat, tz) {
      return format(value, dateFormat, { ...tz ? { timeZone: tz } : {} });
    },
    getBase() {
      return _.noop();
    }
  },

  //                                 888
  //                                 888
  //                                 888
  // 88888b.  888  888 88888b.d88b.  88888b.   .d88b.  888d888
  // 888 "88b 888  888 888 "888 "88b 888 "88b d8P  Y8b 888P"
  // 888  888 888  888 888  888  888 888  888 88888888 888
  // 888  888 Y88b 888 888  888  888 888 d88P Y8b.     888
  // 888  888  "Y88888 888  888  888 88888P"   "Y8888  888
  number: {
    id: 'number',
    description: 'a real number',
    is(value) {
      return _.isNumber(value) && !_.isNaN(parseFloat(value)) && value !== Infinity && value !== -Infinity;
    },
    to(value) {

      // Check for Infinity and NaN
      if (value === Infinity || value === -Infinity) {
        throw new Error('E_runtimeInputTypeCoercionError');
      }
      if (_.isNaN(value)) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }

      // Check for `null` and `undefined`
      if (_.isNull(value)) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }
      if (_.isUndefined(value)) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }

      // Tolerate Date instances.
      if (_.isDate(value)) {
        return value.getTime();
      }

      // Reject any other JavaScript objects (includes dictionaries, arrays, functions, regexps, etc.)
      if (_.isObject(value)) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }

      if (TypeCoerce.number.is(value)) {
        return value;
      }
      if (TypeCoerce.boolean.is(value)) {
        return value ? 1 : 0;
      }
      if (TypeCoerce.string.is(value)) {

        // Is this a string that appears to be a number?
        const isStringThatAppearsToBeNumber = (function _getIsStringThatAppearsToBeNumber(internalValue) {
          if (!_.isString(internalValue)) {
            return false;
          }

          // (this is an exception... apparently doing `+''` and `+' '` in javascript results in `0`)
          if (internalValue === '' || internalValue.match(/\s/)) {
            return false;
          }

          // General case:
          if (_.isNaN(Number(internalValue))) {
            return false;
          }
          if (Number(internalValue) === Infinity) {
            return false;
          }
          return Number(internalValue) !== -Infinity;


        })(value);

        if (!isStringThatAppearsToBeNumber) {
          throw new Error('E_runtimeInputTypeCoercionError');
        }

        return Number(value);
      }

      const num = Number(value);
      if (!_.isNumber(num)) {
        throw new TypeError('E_runtimeInputTypeCoercionError');
      }
      return num === 0 && !value.match(/^0+$/) ? 0 : num;
    },
    getBase() {
      return 0;
    },
    format(value, mask) {
      let i = 0;
      const valueString = this.to(value);
      return mask.replaceAll('#', () => valueString[i++]);
    }
  },


  // 888                        888
  // 888                        888
  // 888                        888
  // 88888b.   .d88b.   .d88b.  888  .d88b.   8888b.  88888b.
  // 888 "88b d88""88b d88""88b 888 d8P  Y8b     "88b 888 "88b
  // 888  888 888  888 888  888 888 88888888 .d888888 888  888
  // 888 d88P Y88..88P Y88..88P 888 Y8b.     888  888 888  888
  // 88888P"   "Y88P"   "Y88P"  888  "Y8888  "Y888888 888  888
  boolean: {
    id: 'boolean',
    description: 'true or false',
    is: _.isBoolean,
    to(value) {
      switch (value) {
        case true:
        case 1:
        case '1':
        case 'true':
          return true;
        case false:
        case 0:
        case '0':
        case 'false':
          return false;
        default:
          throw new Error('E_runtimeInputTypeCoercionError');
      }
    },
    getBase() {
      return false;
    },
    format(value, trueFalseValues) {
      const [truthy, falsey] = trueFalseValues.includes('|') ? trueFalseValues.split('|') : ['True', 'False'];
      return value ? truthy : falsey;
    }
  }

};


// Abbreviation aliases for backwards compat.
TypeCoerce.str = TypeCoerce.string;
TypeCoerce.day = TypeCoerce.date;
TypeCoerce.datetime = TypeCoerce.date;
TypeCoerce.num = TypeCoerce.number;
TypeCoerce.bool = TypeCoerce.boolean;
TypeCoerce.arr = TypeCoerce.array;
TypeCoerce.dict = TypeCoerce.dictionary;

// (Mi)spelling aliases
TypeCoerce.lambda = TypeCoerce.lamda;

// Case-folding aliases
TypeCoerce.JSON = TypeCoerce.json;

export { TypeCoerce };

